import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, EntityKeyByReference, Note, PaginatedApiResponse } from '@card-software/library';
import { Requisition, RequisitionEntry, RequisitionSearchCommand } from '../../models/requisitions/requisition';
import { EntityKeyByNumber } from '../../models/inventory/keys/entity-key';
import { InventoryTransaction } from '../../models/inventory/inventory-transaction';
import { ItemMaster } from '../../models/item/item-master';

@Injectable({
  providedIn: 'root',
})
export class RequisitionProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/requisition';

  public create(body: Requisition): Observable<ApiResponse<Requisition>> {
    return this.httpClient.post<ApiResponse<Requisition>>(this.baseUrl, body);
  }

  public update(id: string, body: Requisition): Observable<ApiResponse<Requisition>> {
    return this.httpClient.put<ApiResponse<Requisition>>(this.baseUrl + '/' + id, body);
  }

  public getById(id: string, materialize = true): Observable<ApiResponse<Requisition>> {
    return this.httpClient.get<ApiResponse<Requisition>>(this.baseUrl + '/' + id + '?materialize=' + materialize);
  }

  public getAll(): Observable<ApiResponse<Requisition[]>> {
    return this.httpClient.get<ApiResponse<Requisition[]>>(this.baseUrl + '/all');
  }

  public getAdditionalItems(): Observable<ApiResponse<ItemMaster[]>> {
    return this.httpClient.get<ApiResponse<ItemMaster[]>>(this.baseUrl + '/additional-items');
  }

  public search(
    command: RequisitionSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PaginatedApiResponse<Requisition>>> {
    let queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      queryParams += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }

    return this.httpClient.post<ApiResponse<PaginatedApiResponse<Requisition>>>(this.baseUrl + '/search' + queryParams, command);
  }

  public updateStatus(id: string, status: number): Observable<ApiResponse<Requisition>> {
    const payload = {
      newStatus: status,
    };
    return this.httpClient.patch<ApiResponse<Requisition>>(this.baseUrl + '/' + id + '/status', payload);
  }

  public createFromEntityKey(
    entityKey: EntityKeyByReference,
    fromLocationCode: string,
    toLocationCode: string,
  ): Observable<ApiResponse<Requisition>> {
    return this.httpClient.post<ApiResponse<Requisition>>(
      `${this.baseUrl}/entity-key/create?fromLocationCode=${encodeURIComponent(fromLocationCode)}&toLocationCode=${encodeURIComponent(toLocationCode)}`,
      entityKey,
    );
  }

  public patchEntries(id: string, entries: RequisitionEntry[]): Observable<ApiResponse<Requisition>> {
    return this.httpClient.patch<ApiResponse<Requisition>>(`${this.baseUrl}/${id}/entries`, entries);
  }

  public addLotItem(id: string, lotItemKey: EntityKeyByNumber, targetItemCode: string): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.post<ApiResponse<InventoryTransaction>>(
      `${this.baseUrl}/${id}/lot-item/add?targetItemCode=${encodeURIComponent(targetItemCode)}`,
      lotItemKey,
    );
  }

  public removeLotItem(id: string, lotItemKey: EntityKeyByNumber): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.post<ApiResponse<InventoryTransaction>>(`${this.baseUrl}/${id}/lot-item/remove`, lotItemKey);
  }

  public addNotes(id: string, notes: Note): Observable<ApiResponse<Requisition>> {
    return this.httpClient.patch<ApiResponse<Requisition>>(this.baseUrl + '/' + id + '/notes', notes);
  }
}
